import React, { useState, useEffect } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { ArrowLeft, Zap, Users, Clock, TrendingUp, Calendar, Star, Award, Target, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const BreakthroughsReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeFilter, setTimeFilter] = useState('This Year');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Mock data for breakthrough sessions (community calls led by experts)
  const [breakthroughData, setBreakthroughData] = useState({
    overview: {
      totalSessions: 0,
      totalParticipants: 0,
      avgParticipantsPerSession: 0,
      totalDuration: 0,
      avgSessionDuration: 0,
      expertLedSessions: 0,
      participantSatisfaction: 0
    },
    sessionsByExpert: [],
    sessionsData: [],
    participationData: [],
    satisfactionData: [],
    expertPerformance: []
  });

  // Mock data initialization
  useEffect(() => {
    // Initialize with sample data for breakthrough sessions
    const sampleData = {
      overview: {
        totalSessions: 48,
        totalParticipants: 1247,
        avgParticipantsPerSession: 26.0,
        totalDuration: 2880, // minutes
        avgSessionDuration: 60.0,
        expertLedSessions: 48,
        participantSatisfaction: 4.7
      },
      sessionsByExpert: [
        { name: 'Sarah Johnson', value: 12, color: '#8884d8' },
        { name: 'Mike Chen', value: 10, color: '#82ca9d' },
        { name: 'Emily Rodriguez', value: 8, color: '#ffc658' },
        { name: 'David Kim', value: 7, color: '#ff7300' },
        { name: 'Lisa Wang', value: 6, color: '#00ff00' },
        { name: 'Others', value: 5, color: '#0088fe' }
      ],
      sessionsData: [
        { period: 'Jan', sessions: 4, participants: 98, satisfaction: 4.6 },
        { period: 'Feb', sessions: 5, participants: 127, satisfaction: 4.7 },
        { period: 'Mar', sessions: 3, participants: 78, satisfaction: 4.5 },
        { period: 'Apr', sessions: 6, participants: 156, satisfaction: 4.8 },
        { period: 'May', sessions: 4, participants: 105, satisfaction: 4.6 },
        { period: 'Jun', sessions: 5, participants: 132, satisfaction: 4.7 }
      ],
      participationData: [
        { name: 'Regular Attendees', value: 45, color: '#10b981' },
        { name: 'Occasional Attendees', value: 35, color: '#f59e0b' },
        { name: 'New Participants', value: 20, color: '#3b82f6' }
      ],
      satisfactionData: [
        { period: 'Jan', rating: 4.6, feedback: 23 },
        { period: 'Feb', rating: 4.7, feedback: 28 },
        { period: 'Mar', rating: 4.5, feedback: 19 },
        { period: 'Apr', rating: 4.8, feedback: 31 },
        { period: 'May', rating: 4.6, feedback: 25 },
        { period: 'Jun', rating: 4.7, feedback: 29 }
      ],
      expertPerformance: [
        {
          id: 1,
          name: 'Sarah Johnson',
          sessions: 12,
          totalParticipants: 312,
          avgParticipants: 26.0,
          avgSatisfaction: 4.8,
          totalDuration: 720,
          topics: ['Business Growth', 'Marketing Strategy', 'Leadership'],
          status: 'Active'
        },
        {
          id: 2,
          name: 'Mike Chen',
          sessions: 10,
          totalParticipants: 258,
          avgParticipants: 25.8,
          avgSatisfaction: 4.6,
          totalDuration: 600,
          topics: ['Technology', 'Operations', 'Process Improvement'],
          status: 'Active'
        },
        {
          id: 3,
          name: 'Emily Rodriguez',
          sessions: 8,
          totalParticipants: 204,
          avgParticipants: 25.5,
          avgSatisfaction: 4.7,
          totalDuration: 480,
          topics: ['Sales', 'Customer Relations', 'Team Building'],
          status: 'Active'
        },
        {
          id: 4,
          name: 'David Kim',
          sessions: 7,
          totalParticipants: 189,
          avgParticipants: 27.0,
          avgSatisfaction: 4.9,
          totalDuration: 420,
          topics: ['Finance', 'Analytics', 'Strategic Planning'],
          status: 'Active'
        },
        {
          id: 5,
          name: 'Lisa Wang',
          sessions: 6,
          totalParticipants: 156,
          avgParticipants: 26.0,
          avgSatisfaction: 4.5,
          totalDuration: 360,
          topics: ['HR', 'Training', 'Development'],
          status: 'Active'
        }
      ]
    };

    setBreakthroughData(sampleData);
    setLoading(false);
  }, []);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const exportToPDF = () => {
    const pdf = new jsPDF();
    pdf.setFontSize(20);
    pdf.text('Breakthrough Sessions Report', 20, 20);
    
    pdf.setFontSize(12);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    pdf.text(`Time Period: ${timeFilter}`, 20, 40);
    
    pdf.text(`Total Sessions: ${breakthroughData.overview.totalSessions}`, 20, 60);
    pdf.text(`Total Participants: ${breakthroughData.overview.totalParticipants}`, 20, 70);
    pdf.text(`Average Participants per Session: ${breakthroughData.overview.avgParticipantsPerSession}`, 20, 80);
    pdf.text(`Average Session Duration: ${formatDuration(breakthroughData.overview.avgSessionDuration)}`, 20, 90);
    pdf.text(`Participant Satisfaction: ${breakthroughData.overview.participantSatisfaction}/5.0`, 20, 100);
    
    pdf.save('breakthrough-sessions-report.pdf');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading breakthrough session data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-8">
          <BackButton />
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
            Breakthrough Sessions Reports
          </h1>
          <div className="w-16"></div> {/* Spacer for centering */}
        </div>

        {/* Time Filter */}
        <div className="mb-8 flex justify-center">
          <div className="flex space-x-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-1 shadow-sm">
            {['This Month', 'This Quarter', 'This Year', 'All Time'].map((period) => (
              <button
                key={period}
                onClick={() => setTimeFilter(period)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeFilter === period
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Zap className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {breakthroughData.overview.totalSessions}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Participants</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {breakthroughData.overview.totalParticipants.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Clock className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Duration</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatDuration(breakthroughData.overview.avgSessionDuration)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Star className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {breakthroughData.overview.participantSatisfaction}/5.0
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Participation Over Time */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Participation Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={breakthroughData.sessionsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sessions" stroke="#8884d8" strokeWidth={2} name="Sessions" />
                <Line type="monotone" dataKey="participants" stroke="#82ca9d" strokeWidth={2} name="Participants" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Expert Distribution */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Sessions Led by Expert
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={breakthroughData.sessionsByExpert}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {breakthroughData.sessionsByExpert.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Satisfaction Trends */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Participant Satisfaction Trends
            </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={breakthroughData.satisfactionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis domain={[4.0, 5.0]} />
              <Tooltip />
              <Bar dataKey="rating" fill="#8884d8" name="Satisfaction Rating" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Expert Performance Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Expert Performance Overview
            </h3>
            <button
              onClick={exportToPDF}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Export PDF
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Expert
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Avg Participants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Satisfaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Topics
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {breakthroughData.expertPerformance.map((expert) => (
                  <tr key={expert.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {expert.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {expert.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {expert.sessions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {expert.avgParticipants}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 mr-1" />
                        {expert.avgSatisfaction}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex flex-wrap gap-1">
                        {expert.topics.map((topic, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        expert.status === 'Active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {expert.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Participation Breakdown */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Participant Engagement Breakdown
            </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={breakthroughData.participationData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {breakthroughData.participationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default BreakthroughsReports;